import React, {useEffect, useMemo} from 'react'
import {Outlet, useSearchParams} from 'react-router-dom'
import {AuthLayer} from '@src/Layers/AuthLayer/AuthLayer.tsx'
import {GpsInstructionsModals} from "./Components/Modals/GpsInstructionsModals.tsx";
import {GeoState, useGpsStore} from "../../Store/gpsStore.ts";
import {EventsLayer} from "@src/Layers/EventsLayer/EventsLayer.tsx";
import {useNetworkWatcher} from "@src/utils/net.ts";
import {useQueryWithOffline} from "@src/httpClient/utils/queryHelpers.ts";
import {apiClient} from "@src/httpClient/api.ts";
import {formatDate} from "@src/utils/formatDate.ts";
import moment from "moment-timezone";
import styles from "./mainRoute.module.less"
import {useCredentialsStore} from "@src/Store/userStore.ts";
import {ManagerLayer} from "@src/Layers/ManagerLayer/ManagerLayer.tsx";
import {usePwaUpdate} from "@src/utils/usePwaUpdate.ts";
import { Modal } from '@src/CommonComponents/Modal/Modal.tsx';
import {Stack} from "@src/CommonComponents/Stack/Stack.tsx";
import {useWorkDaysStore} from "@src/Store/workDaysStore.ts";
import {WorkdayResponse} from "@src/Store/employeesStore.ts";
import {listWorkDays} from "@src/offline/offline.ts";
import {WorkdayWithEmployee} from "@src/httpClient/__generated__/Api.ts";
import {useSyncStore} from "@src/offline/sync.ts";
import {ModalLayer} from "@src/Layers/ModalLayer/ModalLayer.tsx";

enum Modals {
    "update_available"
}

const ModalToast = ({modal, close}: {modal?: Modals, close: () => void}) => {
    if (modal === undefined) return null
    return (
        <Modal onClose={close}>
            {modal == Modals.update_available && (
                <Stack>Update modal</Stack>
            )}
        </Modal>
    )
}


const CommonDataLayer: React.FC<React.PropsWithChildren> = ({children}) => {
    const GPSState = useGpsStore(x => x.state);
    const showGPSInstructions = useGpsStore(x => x.showInstructions);
    const checkGPSState = useGpsStore(x => x.checkState);
    const setWorkDays = useWorkDaysStore(x => x.setWorkdays)
    const setCurrentWorkDay = useWorkDaysStore(x => x.setCurrentWorkDay)
    const syncEmployeesWorkDays = useSyncStore(x => x.syncEmployeesWorkDays);
    const { isUpdateAvailable } = usePwaUpdate();
    const [modal, setModal] = React.useState<Modals | undefined>(undefined);
    const {employee} = useCredentialsStore()

    useEffect(() => {
        if (isUpdateAvailable) {
            console.log("got PWA update this")
            setModal(Modals.update_available)
        }
    }, [isUpdateAvailable]);

    moment.tz.setDefault("Asia/Tokyo");

    const [params] = useSearchParams()

    const searchStr = useMemo(() => {
        const base = new URLSearchParams();
        const date_range_after = params.get("date_range_after")

        if (date_range_after) {
            base.set("date_range_after", date_range_after);
        }

        const date_range_before = params.get("date_range_before")
        if (date_range_before) {
            base.set("date_range_before", date_range_before);
        }
        return base.toString();
    }, [params]);

    const {online} = useNetworkWatcher();

    const {data: workdays} = useQueryWithOffline<WorkdayResponse>(
        ['workdays_employees', searchStr],
        // ONLINE
        async (_, signal) => {
            const result = await apiClient.api.tasWorkdaysList(
                undefined, {search: searchStr, signal}
            )
            return {results: result.data, count: result.data?.length, __source: 'online' as const}
        },
        // OFFLINE
        async (_, _signal) => {
            const result = await listWorkDays(searchStr)
            return {results: result, count: result?.length, __source: 'online' as const}
        },
        (data) => data,
        false
    );

    useEffect(() => {
        if (!workdays?.results || !employee) return
        setWorkDays(workdays?.results);
        if ((workdays as any).__source === 'online') {
            syncEmployeesWorkDays(workdays?.results as WorkdayWithEmployee[])
        }

        if (!employee?.is_manager){
            const today = formatDate(new Date().toString())
            setCurrentWorkDay(workdays?.results?.find(e => formatDate(e?.date) === today))
        }
    }, [employee, workdays]);

    useEffect(() => {
        if (GPSState !== GeoState.granted) checkGPSState()
    }, [GPSState, online]);

    // No upfront permission gate: each check-in/out requests the location itself, which shows the browser prompt
    if (showGPSInstructions) return <GpsInstructionsModals/>

    if (employee?.is_manager)
        return (
            <>
                <ModalToast modal={modal} close={() => setModal(undefined)}/>
                <ManagerLayer>{children}</ManagerLayer>
                <div className={styles.footer}/>
            </>
        )
    return (
        <>
            <ModalToast modal={modal} close={() => setModal(undefined)}/>
            {children}
            <div className={styles.footer}/>
        </>
    )
}

export const MainLayout = () => (
    <AuthLayer>
        <CommonDataLayer>
            <ModalLayer>
                <EventsLayer>
                    <Outlet/>
                </EventsLayer>
            </ModalLayer>
        </CommonDataLayer>
    </AuthLayer>
)
